import React, { useState, useEffect } from "react";
import { 
  Button, 
  ButtonGroup, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import AzureMap from "./AzureMap";
import GoogleMap from "./GoogleMap";
import Navbar from "../NavBar/Navbar";
import axios from "axios";
import { polygon as turfPolygon } from "@turf/turf";
import bboxClip from "@turf/bbox-clip";
import area from "@turf/area";

// Common cluster configuration for both map types
const clusterConfig = {
  enabled: true,
  radius: 25,
  minPoints: 2,
  colors: {
    small: "lightblue",
    medium: "green",
    large: "red"
  },
  sizeRanges: {
    small: 100,
    medium: 750
  },
  sizes: {
    small: 10,
    medium: 15,
    large: 20
  }
};

const MapContainer = () => {
  const [mapType, setMapType] = useState("Azure");
  const [unauthorizedAccess, setUnauthorizedAccess] = useState(false);
  const [pins, setPins] = useState([]);
  const [statusFilters, setStatusFilters] = useState({
    Green: false,
    Yellow: false,
    Red: false,
    GatedFenced: false,
    Garage: false,
    Fee: false,
    NotRegistered: false,
    REIT: false
  });
  const [selectedSalesperson, setSelectedSalesperson] = useState("");
  const [salesList, setSalesList] = useState([]);

  // Isochrone related state - moved from child components
  const [showIsochronePanel, setShowIsochronePanel] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState({ hours: 12, minutes: 0 });
  const [isochronePercentages, setIsochronePercentages] = useState({
    percentage5: 0,
    percentage10: 0,
    percentage15: 0,
    percentage20: 0
  });
  const [mergedIsochrones, setMergedIsochrones] = useState(null);

  // Info dialog state
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // Add state for map position
  const [mapPosition, setMapPosition] = useState({
    center: [-97.5, 39.8],
    zoom: 3.5
  });

  /* ------------------ COMMON UTILITY FUNCTIONS MOVED FROM CHILD COMPONENTS ------------------ */
  // Utility to check if a string value is "true"
  const isTrue = (v) =>
    typeof v === "string" && v.trim().toLowerCase() === "true";

  // Utility to generate the right image ID for map pins based on properties
  const generateImageId = (pin) => {
    let prefix;
    if (pin.StatusReason === "1" && pin.Status === "0") prefix = "yellow-pin";
    else if (
      (pin.StatusReason === "100000000" || pin.StatusReason === "5") &&
      pin.Status === "0"
    )
      prefix = "green-pin";
    else if (pin.StatusReason === "2" && pin.Status === "1")
      prefix = "red-pin";
    else prefix = "unknown-pin";

    const suffixes = [];
    if (pin.StatusReason === "100000000") suffixes.push("inactive");
    if (isTrue(pin.garage)) suffixes.push("garage");
    if (isTrue(pin.GatedOrFenced)) suffixes.push("fence");
    if (isTrue(pin.Fee)) suffixes.push("fee");
    if (isTrue(pin.REIT)) suffixes.push("REIT");
    return `${prefix}${suffixes.length ? "-" + suffixes.join("-") : ""}`;
  };

  // Utility to generate CRM links
  const getCRMLink = (id) => {
    const baseUrl = process.env.REACT_APP_CRM_BASE_URL;
    const appId = process.env.REACT_APP_CRM_APP_ID;
    return `${baseUrl}?etn=cr0f9_property&id=${id}&pagetype=entityrecord&forceUCI=1&appid=${appId}`;
  };

  // Common filter function that determines if a pin passes all selected filters
  const passesFilters = (pin) => {
    const anyStatusSelected = Object.keys(statusFilters)
      .filter(
        (k) =>
          ![
            "GatedFenced",
            "Fee",
            "Garage",
            "NotRegistered",
            "REIT"
          ].includes(k)
      )
      .some((k) => statusFilters[k]);

    let statusMatch = true;
    const reason = pin.StatusReason;
    const status = pin.Status;
    if (anyStatusSelected) {
      statusMatch = false;
      if (
        statusFilters.Green &&
        status === "0" &&
        (reason === "100000000" || reason === "5")
      )
        statusMatch = true;
      if (statusFilters.Yellow && reason === "1" && status === "0")
        statusMatch = true;
      if (statusFilters.Red && reason === "2" && status === "1")
        statusMatch = true;
    }

    const gated = statusFilters.GatedFenced ? isTrue(pin.GatedOrFenced) : true;
    const fee = statusFilters.Fee ? isTrue(pin.Fee) : true;
    const garage = statusFilters.Garage ? isTrue(pin.garage) : true;
    const notReg = statusFilters.NotRegistered
      ? status === "0" && reason === "100000000"
      : true;
    const reit = statusFilters.REIT ? isTrue(pin.REIT) : true;
    const sales = selectedSalesperson
      ? pin.Salesperson === selectedSalesperson
      : true;

    return (
      statusMatch && gated && fee && garage && notReg && reit && sales
    );
  };

  /* ------------------ DISABLE SCROLLING ----------------- */
  useEffect(() => {
    // Disable scrolling when component mounts
    document.body.style.overflow = 'hidden';
    
    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  /* ------------------ FETCH DATA ----------------------- */
  const fetchData = async () => {
    try {
      const hostPath = process.env.REACT_APP_API_BASE_URL;
      const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;
      
      const res = await fetch(`${hostPath}/api/GetAllSales`, {
        method: "GET",
        headers: {
          "x-functions-key": hostHeader,
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) {
        if (res.status === 401) setUnauthorizedAccess(true);
        throw new Error(`Fetch failed: ${res.status}`);
      }
      const data = await res.json();
      setPins(data);
      const s = new Set();
      data.forEach((i) => s.add(i.Salesperson));
      setSalesList(Array.from(s));
    } catch (e) {
      console.error("Error fetching data:", e);
    }
  };

  /* ------------------ POLL DATA ------------------- */
  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 21600000);
    return () => clearInterval(id);
  }, []);

  const handleStatusFilterChangeButton = (k) =>
    setStatusFilters((prev) => ({ ...prev, [k]: !prev[k] }));
    
  /* ---------------- ISOCHRONE GENERATION ---------------- */
  const handleGenerateIsochrone = async (currentBounds, getVisibleGreenPins) => {
    try {
      setMergedIsochrones(null);

      const hostPath = process.env.REACT_APP_API_BASE_URL;
      const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;

      // Get visible green pins - implementation varies between map types
      const points = getVisibleGreenPins(currentBounds);
      
      if (!points || points.length === 0) return;

      const localDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        selectedTime.hours,
        selectedTime.minutes
      );
      const departAt = localDate.toISOString().slice(0, 16) + ":00";

      const res = await axios.post(
        `${hostPath}/api/GetCustomIsochrones`,
        { Points: points, DepartAt: departAt },
        { headers: { "x-functions-key": hostHeader } }
      );
      setMergedIsochrones(res.data.mergedIsochrones);
    } catch (e) {
      console.error("Isochrone error:", e);
    }
  };
  
  /* ---------------- UPDATE ISOCHRONE PERCENTAGES ---------------- */
  const updateIsochronePercentages = (bounds) => {
    if (!mergedIsochrones || !bounds) return;
    
    let a = { 5: 0, 10: 0, 15: 0, 20: 0 };
    
    Object.entries(mergedIsochrones).forEach(([dur, polys]) => {
      polys.forEach((poly) => {
        const c = poly.map((pt) => [pt.Lng || pt.lng, pt.Lat || pt.lat]);
        try {
          const clip = bboxClip(turfPolygon([c]), bounds);
          a[dur] += area(clip);
        } catch (err) {
          // Silently handle any geometry errors
        }
      });
    });
    
    const total =
      a[5] +
      Math.max(a[10] - a[5], 0) +
      Math.max(a[15] - a[10], 0) +
      Math.max(a[20] - a[15], 0);
      
    setIsochronePercentages({
      percentage5: total ? ((a[5] / total) * 100).toFixed(2) : 0,
      percentage10: total ? ((Math.max(a[10] - a[5], 0) / total) * 100).toFixed(2) : 0,
      percentage15: total ? ((Math.max(a[15] - a[10], 0) / total) * 100).toFixed(2) : 0,
      percentage20: total ? ((Math.max(a[20] - a[15], 0) / total) * 100).toFixed(2) : 0
    });
  };

  // Add date validation helper
  const getValidDate = (date) => {
    return date instanceof Date && !isNaN(date) ? date : new Date();
  };

  /* ------------------ ISOCHRONE UI ELEMENTS ------------------ */
  const isochroneUiElements = () => {
    const validDate = getValidDate(selectedDate);
    return (
      showIsochronePanel && (
        <>
          {/* --- DATE/TIME PICKER --- */}
          <div
            style={{
              position: "absolute",
              top: "30%",
              left: "2%", // Consistent left positioning at 2%
              background: "white",
              padding: "1.5%",
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1200,
              width: "20%", // Consistent width at 20%
              height: "auto",
              minWidth: "300px"
            }}
          >
            <label style={{ display: "block", marginBottom: 10, fontWeight: "500" }}>
              Select Date:
              <input
                type="date"
                value={validDate.toISOString().split("T")[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                style={{ width: "100%", padding: 10, marginTop: 8, borderRadius: 4, border: "1px solid #ccc" }}
              />
            </label>
            <label style={{ display: "block", margin: "20px 0 10px", fontWeight: "500" }}>
              Select Time:
              <input
                type="time"
                value={`${selectedTime.hours.toString().padStart(2, "0")}:${selectedTime.minutes.toString().padStart(2, "0")}`}
                onChange={(e) => {
                  const [hh, mm] = e.target.value.split(":");
                  setSelectedTime({
                    hours: Number(hh),
                    minutes: Number(mm)
                  });
                }}
                style={{ width: "100%", padding: 10, marginTop: 8, borderRadius: 4, border: "1px solid #ccc" }}
              />
            </label>
            <Button
              variant="contained"
              sx={{
                marginTop: 2,
                width: "100%",
                backgroundColor: '#4285f4',
                '&:hover': { backgroundColor: '#3367d6' },
                textTransform: 'uppercase',
                fontWeight: 'normal',
                padding: "10px 0"
              }}
              onClick={() => {
                if (mapRef.current && typeof mapRef.current.getCurrentBounds === 'function'
                   && typeof mapRef.current.getVisibleGreenPins === 'function') {
                  const bounds = mapRef.current.getCurrentBounds();
                  const pins = mapRef.current.getVisibleGreenPins(bounds);
                  handleGenerateIsochrone(bounds, () => pins);
                }
              }}
              id="generate-isochrone-button"
            >
              GENERATE ISOCHRONE
            </Button>
            
            {/* New Refresh Data Button */}
            <Button
              variant="contained"
              sx={{
                marginTop: 1.5,
                width: "100%",
                backgroundColor: '#4285f4',
                '&:hover': { backgroundColor: '#3367d6' },
                textTransform: 'uppercase',
                fontWeight: 'normal',
                padding: "10px 0"
              }}
              onClick={() => {
                fetchData();
                setMergedIsochrones(null);
                setIsochronePercentages({
                  percentage5: 0,
                  percentage10: 0,
                  percentage15: 0,
                  percentage20: 0
                });
              }}
              id="refresh-data-button"
            >
              clear isochrones
            </Button>
          </div>

          {/* --- COVERAGE LEGEND --- */}
          <div
            style={{
              position: "absolute",
              bottom: "5%",
              left: "2%", // Consistent left positioning at 2%
              background: "white",
              padding: "1%",
              borderRadius: 5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1200,
              width: "20%", // Consistent width at 20%
              minWidth: "300px"
            }}
          >
            <div style={{ marginBottom: 5, fontWeight: "bold" }}>
              Isochrone Coverage (%)
            </div>
            {[
              [
                "rgba(76, 175, 80, 1)",
                "<5 minutes",
                isochronePercentages.percentage5
              ],
              [
                "rgba(251, 198, 47, 1)",
                "<10 minutes",
                isochronePercentages.percentage10
              ],
              [
                "rgba(247, 139, 7, 1)",
                "<15 minutes",
                isochronePercentages.percentage15
              ],
              [
                "rgba(219, 23, 19, 1)",
                "<20 minutes",
                isochronePercentages.percentage20
              ],
              [
                "rgb(246, 240, 240)",
                ">20 minutes",
                ""  // Removed percentage value since it's not calculated
              ]
            ].map(([c, label, val]) => (
              <div
                key={label}
                style={{ display: "flex", alignItems: "center", marginTop: 5 }}
              >
                <div
                  style={{
                    width: 15,
                    height: 15,
                    background: c,
                    marginRight: 5
                  }}
                />
                <span>
                  {label}{val !== "" ? `: ${val}%` : ""}
                </span>
              </div>
            ))}
          </div>
        </>
      )
    );
  };

  // Reference to the active map component
  const mapRef = React.useRef(null);

  if (unauthorizedAccess) {
    return (
      <div className="unauthorized-container">
        <h2>Unauthorized Access</h2>
        <p>You do not have permission to view this content.</p>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      
      {/* --- INFO BUTTON --- */}
      <div
        style={{
          position: 'absolute',
          top: '17%',
          left: '2%',
          zIndex: 4000,
          display: 'flex',
          gap: '10px'
        }}
      >
        <IconButton
          onClick={() => setInfoDialogOpen(true)}
          sx={{
            backgroundColor: 'white',
            padding: '8px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
        >
          <InfoIcon />
        </IconButton>
      </div>

      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          How to Use the Map
          <IconButton onClick={() => setInfoDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemText
                primary="Search Locations"
                secondary="Use the search box to find specific locations on the map"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Isochrone Coverage"
                secondary="Shows travel time zones (5, 10, 15, 20 minutes) from selected points"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Date/Time Selection"
                secondary="Choose specific dates to view historical data and coverage"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Map Controls"
                secondary="Zoom in/out using +/- buttons or mouse wheel. Click and drag to pan"
              />
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
      
      {/* -------- MAP TYPE TOGGLE -------- */}
      <div
        style={{
          position: "fixed",
          top: "30%",
          right: "2%",
          zIndex: 4000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <ButtonGroup 
          variant="contained" 
          size="small"
          sx={{
            borderRadius: '0px',
            overflow: 'hidden',
            '& .MuiButton-root': {
              minWidth: '80px', 
              height: '32px',
              textTransform: 'none',
              px: 2,
              backgroundColor: 'white',
              color: '#666',
              border: '1px solid #ddd',
              fontSize: '0.875rem',
              fontWeight: 400,
              '&:first-of-type': {
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
              },
              '&:last-of-type': {
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
              },
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
              '&.active': {
                backgroundColor: '#1976d2',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1565c0',
                }
              }
            }
          }}
        >
          <Button
            onClick={() => setMapType("Azure")}
            className={mapType === "Azure" ? "active" : ""}
          >
            Azure
          </Button>
          <Button
            onClick={() => setMapType("Google")}
            className={mapType === "Google" ? "active" : ""}
          >
            Google
          </Button>
        </ButtonGroup>
      </div>

      {/* --- ISOCHRONE PANEL TOGGLE --- */}
      <div
        style={{
          position: "absolute",
          top: "23%",
          left: "2%", // Consistent left positioning at 2%
          zIndex: 1000
        }}
      >
        <Button
          variant="contained"
          sx={{
            width: '20%', // Consistent width at 20%
            minWidth: '300px',
            height: 'auto',
            padding: '10px 15px',
            fontSize: '1rem',
            backgroundColor: '#4285f4',
            '&:hover': {
              backgroundColor: '#3367d6'
            },
            textTransform: 'uppercase',
            fontWeight: 'normal'
          }}
          onClick={() => setShowIsochronePanel((p) => !p)}
        >
          {showIsochronePanel
            ? "HIDE ISOCHRONE PANEL"
            : "SHOW ISOCHRONE PANEL"}
        </Button>
      </div>

      {/* Shared Isochrone UI Elements */}
      {isochroneUiElements()}

      {mapType === "Azure" ? (
        <AzureMap 
          pins={pins}
          statusFilters={statusFilters}
          handleStatusFilterChangeButton={handleStatusFilterChangeButton}
          salesList={salesList}
          selectedSalesperson={selectedSalesperson}
          setSelectedSalesperson={setSelectedSalesperson}
          fetchData={fetchData}
          ref={mapRef}
          // Isochrone props
          mergedIsochrones={mergedIsochrones}
          updateIsochronePercentages={updateIsochronePercentages}
          // Common utility functions passed as props
          isTrue={isTrue}
          generateImageId={generateImageId}
          getCRMLink={getCRMLink}
          passesFilters={passesFilters}
          // Add cluster configuration
          clusterConfig={clusterConfig}
          initialPosition={mapPosition}
          onPositionChange={(newPos) => setMapPosition(newPos)}
        />
      ) : (
        <GoogleMap 
          pins={pins}
          statusFilters={statusFilters}
          handleStatusFilterChangeButton={handleStatusFilterChangeButton}
          salesList={salesList}
          selectedSalesperson={selectedSalesperson}
          setSelectedSalesperson={setSelectedSalesperson}
          fetchData={fetchData}
          ref={mapRef}
          // Isochrone props
          mergedIsochrones={mergedIsochrones}
          updateIsochronePercentages={updateIsochronePercentages}
          // Common utility functions passed as props
          isTrue={isTrue}
          generateImageId={generateImageId}
          getCRMLink={getCRMLink}
          passesFilters={passesFilters}
          // Add cluster configuration
          clusterConfig={clusterConfig}
          initialPosition={mapPosition}
          onPositionChange={(newPos) => setMapPosition(newPos)}
        />
      )}
    </>
  );
};

export default MapContainer;